import { atom, getDefaultStore } from 'jotai';

export const IDENTIFY_SPEAKERS_DEFAULT_LEFTOVER = 'heidi:identify-speakers-default';

const defaultStorage = (): Pick<Storage, 'getItem' | 'removeItem'> | undefined =>
  typeof window !== 'undefined' ? window.localStorage : undefined;

const readIdentifySpeakersLeftover = (
  storage: Pick<Storage, 'getItem'> | undefined
): string | null => {
  try {
    return storage?.getItem(IDENTIFY_SPEAKERS_DEFAULT_LEFTOVER) ?? null;
  } catch {
    return null;
  }
};

/**
 * Drop the previous client-store key. Callers that write the preference
 * (toggle + leftover migrate) must spend the stamp so a later unset GET
 * cannot PATCH off over an on the user already chose.
 */
export const dropIdentifySpeakersLeftover = (
  storage: Pick<Storage, 'removeItem'> | undefined = defaultStorage()
): void => {
  try {
    storage?.removeItem(IDENTIFY_SPEAKERS_DEFAULT_LEFTOVER);
  } catch {
    // Private mode / quota: leftover stays; next hydrate retries.
  }
};

/**
 * The previous client store wrote `'false'` here when Identify speakers was
 * off. First paint must match that leftover until GET /user/ hydrates, so
 * leftover-off users do not diarise for one request.
 */
export const leftoverIdentifySpeakersMeansOff = (
  storage: Pick<Storage, 'getItem'> | undefined = defaultStorage()
): boolean => readIdentifySpeakersLeftover(storage) === 'false';

/**
 * Account-level Identify speakers preference. Session defaults and the
 * in-session Transcript dialog share this atom. Hydrate writes only a
 * boolean from GET /user/; leftover `'false'` migrates once then drops.
 *
 * MOBILE-PARITY-DEBT: B2 — `diarise` is still a client request param.
 * Mobile must read `session_settings.identify_speakers` (null/absent → on)
 * until the backend owns that default. Owners: ml-scribe session_settings;
 * iOS/Android diarisation.
 */
export const identifySpeakersUserDefaultAtom = atom(!leftoverIdentifySpeakersMeansOff());

export const resolveIdentifySpeakersDefault = (
  value: boolean | null | undefined
): boolean => value ?? true;

/**
 * The boolean PATCH /user/ actually persisted. Unset/null is undefined so a
 * GET echo without the field cannot be mistaken for a write.
 */
export type IdentifySpeakersSettingsHost = {
  session_settings?: { identify_speakers?: boolean | null } | null;
};

export const identifySpeakersFromUserResponse = (
  user: IdentifySpeakersSettingsHost | null | undefined
): boolean | undefined => {
  const value = user?.session_settings?.identify_speakers;
  return typeof value === 'boolean' ? value : undefined;
};

/**
 * Project a persisted Identify speakers boolean onto a cached GET /user/
 * record. Leaves the cache alone when there is no record yet or the PATCH
 * body did not echo a boolean.
 */
export const mergeIdentifySpeakersIntoUserCache = <
  T extends IdentifySpeakersSettingsHost,
>(
  oldData: T | undefined,
  identifySpeakers: boolean | undefined
): T | undefined => {
  if (!oldData || identifySpeakers === undefined) {
    return oldData;
  }
  return {
    ...oldData,
    session_settings: {
      ...(oldData.session_settings ?? {}),
      identify_speakers: identifySpeakers,
    },
  };
};

type HydrateIdentifySpeakersOptions = {
  isUpdatingSettings?: boolean;
  persistFalse?: () => Promise<unknown>;
  storage?: Pick<Storage, 'getItem' | 'removeItem'>;
};

let leftoverFalsePersistInFlight = false;

/**
 * Project GET /user/ `session_settings.identify_speakers` onto the atom.
 * Leftover `'false'` with no server boolean PATCHes once; the key stays
 * until that write succeeds (`persistFalse` === `false` retries). Any other
 * resolved value spends the leftover. Unset without leftover-false is a
 * no-op. Leftover-false does not restamp an already-on atom.
 */
export const hydrateIdentifySpeakersUserDefaultFromSessionSettings = (
  serverValue: boolean | null | undefined,
  options: HydrateIdentifySpeakersOptions = {}
): Promise<void> => {
  if (options.isUpdatingSettings || leftoverFalsePersistInFlight) {
    return Promise.resolve();
  }

  const storage = options.storage ?? defaultStorage();
  const stored = readIdentifySpeakersLeftover(storage);
  const serverIsUnset = serverValue === null || serverValue === undefined;
  const shouldMigrateFalse = stored === 'false' && serverIsUnset;

  if (shouldMigrateFalse) {
    if (getDefaultStore().get(identifySpeakersUserDefaultAtom) === true) {
      dropIdentifySpeakersLeftover(storage);
      return Promise.resolve();
    }
    getDefaultStore().set(identifySpeakersUserDefaultAtom, false);
    if (!options.persistFalse || leftoverFalsePersistInFlight) {
      return Promise.resolve();
    }
    leftoverFalsePersistInFlight = true;
    try {
      return Promise.resolve(options.persistFalse())
        .then((didPersist) => {
          if (didPersist === false) {
            return;
          }
          dropIdentifySpeakersLeftover(storage);
        })
        .catch(() => {
          // Leave the key so the next GET /user/ can retry migrate.
        })
        .finally(() => {
          leftoverFalsePersistInFlight = false;
        });
    } catch {
      leftoverFalsePersistInFlight = false;
      return Promise.resolve();
    }
  }

  if (serverIsUnset) {
    if (stored != null) {
      dropIdentifySpeakersLeftover(storage);
    }
    return Promise.resolve();
  }

  getDefaultStore().set(identifySpeakersUserDefaultAtom, serverValue);

  if (stored != null) {
    dropIdentifySpeakersLeftover(storage);
  }

  return Promise.resolve();
};
